/**
 * Photo AI page (INVI HQ, operator-only). Holds the global, non-per-dealer
 * concerns of the photo pipeline:
 *
 *   1. Reference Library — every image type and its per-body-style
 *      reference images. Drives CLIP classification.
 *   2. Test Classification — drop a photo, see how the matcher would tag it
 *      against the whole library.
 *
 * The per-dealer capture/publish ordering and dealer plates live on the
 * separate Photo Configuration page. This page is not group-scoped.
 *
 * Tab state mirrors into URL search params so refresh keeps the operator
 * in place.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { ChevronRight, FolderUp } from "lucide-react"

import Layout from "../../components/Layout"
import { ImageTypeEditorModal } from "../../components/photoConfiguration/ImageTypeEditorModal"
import {
  ACCEPTED_IMAGE_TYPES,
  ReferenceStatusPill,
  classifyContainerStyle,
  classifyContextLabelStyle,
  classifyContextRowStyle,
  classifyContextValueStyle,
  classifyDescriptionCardStyle,
  classifyDescriptionLabelStyle,
  classifyDescriptionParagraphStyle,
  classifyLeftColumnStyle,
  classifyRightColumnStyle,
  containerStyle,
  emptyStateStyle,
  emptyStateTextStyle,
  errorBannerStyle,
  hiddenInputStyle,
  libraryListHeaderStyle,
  libraryRowChevronCellStyle,
  libraryRowNameCellStyle,
  libraryRowNameStyle,
  libraryRowStatusCellStyle,
  libraryRowStyle,
  libraryRowThumbnailCellStyle,
  libraryRowThumbnailImageStyle,
  libraryRowThumbnailPlaceholderStyle,
  libraryToolbarLabelStyle,
  libraryToolbarStyle,
  listColumnLabelStyle,
  listContainerStyle,
  loadingTextStyle,
  matchDisplayNameStyle,
  matchesListStyle,
  matchNameColumnStyle,
  matchRankStyle,
  matchRowStyle,
  matchSimilarityStyle,
  matchSlugStyle,
  modalDropZoneHintStyle,
  modalDropZoneOverlayHintStyle,
  modalDropZonePlaceholderStyle,
  modalDropZonePreviewImageStyle,
  modalDropZonePreviewWrapperStyle,
  modalDropZoneStyle,
  modalDropZoneTitleStyle,
  pillEmptyStyle,
  primaryButtonStyle,
  resultHeaderLabelStyle,
  resultHeaderRowStyle,
  secondaryButtonStyle,
  similarityBarFillStyle,
  similarityBarTrackStyle,
  statusPillBaseStyle,
  subtitleStyle,
  tabButtonStyle,
  tabsRowStyle,
  titleStyle,
  vehicleTypePillBaseStyle,
  vehicleTypePillRowStyle,
} from "../../components/photoConfiguration/photoConfigShared"
import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import {
  ALL_BODY_STYLES,
  BODY_STYLE_DISPLAY_NAMES,
  BodyStyle,
  ImageType,
  ImageTypeLibrary,
  PhotoConfigurationClassifyResponse,
  VehicleTypeUsage,
  classifyTestPhoto,
  listImageTypes,
} from "../../api/photoConfiguration"

type TabId = "library" | "test_classify"

const TABS: { id: TabId; label: string }[] = [
  { id: "library",        label: "Reference Library" },
  { id: "test_classify",  label: "Test Classification" },
]

const DEFAULT_TAB: TabId = "library"
const VALID_TAB_IDS: ReadonlySet<TabId> = new Set<TabId>(["library", "test_classify"])

// Body-style priority for picking the row thumbnail in the Reference
// Library list. First uploaded reference wins; order mirrors the
// editor modal's slot order and the bucket selector.
const ROW_THUMBNAIL_PRIORITY: BodyStyle[] = [
  "truck", "suv", "sedan", "coupe", "rv", "camper", "trailer",
]


export default function PhotoAIPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get("tab") as TabId | null
  const activeTab: TabId = tabFromUrl && VALID_TAB_IDS.has(tabFromUrl) ? tabFromUrl : DEFAULT_TAB

  const setActiveTab = (next: TabId) => {
    const updated = new URLSearchParams(searchParams)
    if (next === DEFAULT_TAB) updated.delete("tab")
    else updated.set("tab", next)
    setSearchParams(updated, { replace: true })
  }

  const [library, setLibrary] = useState<ImageTypeLibrary | null>(null)
  const [libraryLoading, setLibraryLoading] = useState<boolean>(true)
  const [libraryError, setLibraryError] = useState<string | null>(null)

  const [editingImageType, setEditingImageType] = useState<ImageType | null>(null)
  const [addingImageType, setAddingImageType] = useState<boolean>(false)

  // Only the first load swaps the list for "Loading…". Refreshes after the
  // editor closes run behind the list that's already on screen: blanking it
  // shrank the page and threw the operator back to the top of the library.
  // A failed refresh keeps that list and shows the error above it.
  const loadLibrary = useCallback(async ({ initial = false }: { initial?: boolean } = {}) => {
    if (initial) setLibraryLoading(true)
    setLibraryError(null)
    try {
      const data = await listImageTypes()
      setLibrary(data)
    } catch (err) {
      setLibraryError(err instanceof Error ? err.message : "Failed to load reference library")
      if (initial) setLibrary(null)
    } finally {
      if (initial) setLibraryLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadLibrary({ initial: true })
  }, [loadLibrary])

  const handleImageTypeSaved = async () => {
    setEditingImageType(null)
    setAddingImageType(false)
    await loadLibrary()
  }

  return (
    <Layout>
      <div style={containerStyle}>
        <h1 style={titleStyle}>Photo AI</h1>
        <p style={subtitleStyle}>
          Manage the global image-type library and the classifier's reference images.
        </p>

        <div style={tabsRowStyle}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={tabButtonStyle(activeTab === tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "library" && libraryError && (
          <div style={errorBannerStyle}>{libraryError}</div>
        )}

        {activeTab === "library" && (
          libraryLoading ? (
            <div style={loadingTextStyle}>Loading…</div>
          ) : library ? (
            <ReferenceLibraryView
              library={library}
              onAddImageType={() => setAddingImageType(true)}
              onEditImageType={(it) => setEditingImageType(it)}
            />
          ) : null
        )}

        {activeTab === "test_classify" && (
          libraryLoading ? (
            <div style={loadingTextStyle}>Loading…</div>
          ) : (
            <TestClassificationView library={library} />
          )
        )}

        {(editingImageType || addingImageType) && (
          <ImageTypeEditorModal
            imageType={editingImageType}
            // Close-without-save still refreshes the library — autosave
            // may have already run while the modal was open, and the
            // operator expects the row behind to reflect their work
            // when they close.
            onClose={() => {
              setEditingImageType(null)
              setAddingImageType(false)
              void loadLibrary()
            }}
            onSaved={handleImageTypeSaved}
          />
        )}
      </div>
    </Layout>
  )
}


// ---------------------------------------------------------------------------
// Reference Library tab — clean list view
// ---------------------------------------------------------------------------

interface ReferenceLibraryViewProps {
  library: ImageTypeLibrary
  onAddImageType: () => void
  onEditImageType: (imageType: ImageType) => void
}

function ReferenceLibraryView({
  library,
  onAddImageType,
  onEditImageType,
}: ReferenceLibraryViewProps) {
  // Sort alphabetical by display name for predictable scanning.
  const sorted = useMemo(
    () => [...library.image_types].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    ),
    [library.image_types],
  )

  if (library.image_types.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTextStyle}>
          No image types in the library yet. Add one here to start building the
          shared vocabulary, or seed a dealer's bucket from the Photo
          Configuration page — seeding the canonical list also creates its image
          types here.
        </p>
        <button onClick={onAddImageType} style={secondaryButtonStyle}>
          + Add Image Type
        </button>
      </div>
    )
  }

  return (
    <div>
      <div style={libraryToolbarStyle}>
        <span style={libraryToolbarLabelStyle}>
          {sorted.length} image type{sorted.length === 1 ? "" : "s"} in library
        </span>
        <button onClick={onAddImageType} style={secondaryButtonStyle}>
          + Add Image Type
        </button>
      </div>
      <div style={listContainerStyle}>
        <div style={libraryListHeaderStyle}>
          <span style={listColumnLabelStyle}>Thumbnail</span>
          <span style={listColumnLabelStyle}>Capture Name</span>
          <span style={listColumnLabelStyle}>Status</span>
          <span style={listColumnLabelStyle}></span>
        </div>
        {sorted.map((imageType) => (
          <ReferenceLibraryRow
            key={imageType.id}
            imageType={imageType}
            onClick={() => onEditImageType(imageType)}
          />
        ))}
      </div>
    </div>
  )
}


function ReferenceLibraryRow({
  imageType,
  onClick,
}: {
  imageType: ImageType
  onClick: () => void
}) {
  const [hovered, setHovered] = useState<boolean>(false)
  // A body style can hold several references (listed oldest first); its
  // oldest one is the row thumbnail, matching the capture app's pick.
  const oldestUrlByBodyStyle = useMemo(() => {
    const byBodyStyle = new Map<BodyStyle, string>()
    for (const reference of imageType.references) {
      if (!byBodyStyle.has(reference.body_style)) {
        byBodyStyle.set(reference.body_style, reference.reference_url)
      }
    }
    return byBodyStyle
  }, [imageType.references])

  const thumbnailUrl = useMemo(() => {
    for (const bodyStyle of ROW_THUMBNAIL_PRIORITY) {
      const url = oldestUrlByBodyStyle.get(bodyStyle)
      if (url) return url
    }
    return null
  }, [oldestUrlByBodyStyle])

  const bodyStylesCovered = oldestUrlByBodyStyle.size
  const totalSlots = ALL_BODY_STYLES.length

  return (
    <div
      style={libraryRowStyle(hovered)}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick() }}
    >
      <div style={libraryRowThumbnailCellStyle}>
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={`${imageType.display_name} reference`}
            style={libraryRowThumbnailImageStyle}
          />
        ) : (
          <div style={libraryRowThumbnailPlaceholderStyle} />
        )}
      </div>
      <div style={libraryRowNameCellStyle}>
        <span style={libraryRowNameStyle}>{imageType.display_name}</span>
        {imageType.vehicle_types_in_use.length > 0 && (
          <div style={vehicleTypePillRowStyle}>
            {imageType.vehicle_types_in_use.map((usage) => (
              <VehicleTypePill key={usage.vehicle_type} usage={usage} />
            ))}
          </div>
        )}
      </div>
      <div style={libraryRowStatusCellStyle}>
        <ReferenceStatusPill count={bodyStylesCovered} total={totalSlots} />
      </div>
      <div style={libraryRowChevronCellStyle}>
        <ChevronRight size={16} color={THEME.colors.mediumGray} />
      </div>
    </div>
  )
}


// One pill per vehicle-type bucket that includes the image type in its
// shot list. Color comes from THEME.vehicleTypeColors keyed by slug; an
// unmapped slug (a hypothetical new bucket we haven't themed yet) falls
// back to the neutral status-pill palette so the row still renders.
function VehicleTypePill({ usage }: { usage: VehicleTypeUsage }) {
  const accent = (
    THEME.vehicleTypeColors as Record<string, string | undefined>
  )[usage.vehicle_type]
  if (!accent) {
    return <span style={pillEmptyStyle}>{usage.display_name}</span>
  }
  const style: React.CSSProperties = {
    ...vehicleTypePillBaseStyle,
    background: tint(accent, 0.15),
    color: accent,
  }
  return <span style={style}>{usage.display_name}</span>
}


// ---------------------------------------------------------------------------
// Test Classification — compares against the whole library
// ---------------------------------------------------------------------------

function TestClassificationView({ library }: { library: ImageTypeLibrary | null }) {
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [result, setResult] = useState<PhotoConfigurationClassifyResponse | null>(null)
  const [classifying, setClassifying] = useState<boolean>(false)
  const [classifyError, setClassifyError] = useState<string | null>(null)
  const [dragHover, setDragHover] = useState<boolean>(false)

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const totalReferences = library?.image_types
    .reduce((sum, it) => sum + it.references.length, 0) ?? 0

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null
    setFile(picked)
    setResult(null)
    setClassifyError(null)
  }

  const handleClassify = async () => {
    if (!file) return
    setClassifying(true)
    setClassifyError(null)
    try {
      const response = await classifyTestPhoto(file)
      setResult(response)
    } catch (err) {
      setClassifyError(err instanceof Error ? err.message : "Classification failed")
    } finally {
      setClassifying(false)
    }
  }

  if (totalReferences === 0) {
    return (
      <div style={emptyStateStyle}>
        <p style={emptyStateTextStyle}>
          No references uploaded yet. Switch to the Reference Library tab and upload
          at least one reference image to enable test classification.
        </p>
      </div>
    )
  }

  return (
    <div style={classifyContainerStyle}>
      <div style={classifyLeftColumnStyle}>
        <div style={classifyDescriptionCardStyle}>
          <span style={classifyDescriptionLabelStyle}>What this test does</span>
          <p style={classifyDescriptionParagraphStyle}>
            Drop a sample photo to see how well the system identifies it.
            It compares your photo against every reference you've uploaded
            and shows the three closest matches. Use this to make sure
            your references are working before relying on automatic tagging.
          </p>
        </div>
        <div style={classifyContextRowStyle}>
          <span style={classifyContextLabelStyle}>Comparing against</span>
          <span style={classifyContextValueStyle}>
            {totalReferences} reference{totalReferences === 1 ? "" : "s"} across the
            whole library
          </span>
        </div>
        <label
          style={modalDropZoneStyle(dragHover)}
          onDragOver={(e) => { e.preventDefault(); setDragHover(true) }}
          onDragLeave={() => setDragHover(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragHover(false)
            const dropped = e.dataTransfer.files?.[0]
            if (dropped && dropped.type.startsWith("image/")) {
              setFile(dropped); setResult(null); setClassifyError(null)
            }
          }}
        >
          <input
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            onChange={handleFileChange}
            style={hiddenInputStyle}
          />
          {previewUrl ? (
            <div style={modalDropZonePreviewWrapperStyle}>
              <img src={previewUrl} alt="Test preview" style={modalDropZonePreviewImageStyle} />
              <span style={modalDropZoneOverlayHintStyle}>
                Drop a different image or click to replace
              </span>
            </div>
          ) : (
            <div style={modalDropZonePlaceholderStyle}>
              <span style={modalDropZoneTitleStyle}>
                <FolderUp size={14} strokeWidth={1.75} />
                Drag an image here or click to upload
              </span>
              <span style={modalDropZoneHintStyle}>
                JPEG / PNG / WebP
              </span>
            </div>
          )}
        </label>
        <button
          onClick={handleClassify}
          disabled={!file || classifying}
          style={primaryButtonStyle}
        >
          {classifying ? "Classifying…" : "Classify"}
        </button>
        {classifyError && <div style={errorBannerStyle}>{classifyError}</div>}
      </div>

      <div style={classifyRightColumnStyle}>
        {!result ? (
          <p style={emptyStateTextStyle}>
            Drop a photo and click Classify to see the top three matches.
          </p>
        ) : (
          <ClassifyResultPanel result={result} />
        )}
      </div>
    </div>
  )
}


function ClassifyResultPanel({ result }: { result: PhotoConfigurationClassifyResponse }) {
  const bestStatusStyle: React.CSSProperties = {
    ...statusPillBaseStyle,
    background: result.matched ? THEME.colors.success : THEME.colors.warning,
    color: THEME.colors.text,
  }
  return (
    <div>
      <div style={resultHeaderRowStyle}>
        <span style={resultHeaderLabelStyle}>Result</span>
        <span style={bestStatusStyle}>
          {result.matched
            ? `Matched — ${result.best_match_slug}`
            : `Below threshold (${result.threshold.toFixed(2)})`}
        </span>
      </div>
      <div style={matchesListStyle}>
        {result.matches.length === 0 && (
          <p style={emptyStateTextStyle}>
            No references in the library to compare against.
          </p>
        )}
        {result.matches.map((match, index) => (
          <div key={`${match.slug}-${match.matched_body_style}-${index}`} style={matchRowStyle}>
            <span style={matchRankStyle}>{index + 1}.</span>
            <span style={matchNameColumnStyle}>
              <span style={matchDisplayNameStyle}>{match.display_name}</span>
              <span style={matchSlugStyle}>
                {match.slug} · matched on {BODY_STYLE_DISPLAY_NAMES[match.matched_body_style]} reference
              </span>
            </span>
            <span style={matchSimilarityStyle}>{match.similarity.toFixed(3)}</span>
            <div style={similarityBarTrackStyle}>
              <div style={similarityBarFillStyle(match.similarity)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
